import React, { useState, useRef, useEffect } from 'react';
import { DatePicker, Select } from '@amzn/awsui-components-react';
import '../../FlexiCo/styles.scss';
import { convertTo12HourFormat, convertTo24HourFormat } from '../../utils/generalUtils';

const pad = (n) => String(n).padStart(2, '0');

const TimePicker = ({ value = '00:00', onChange, disabled = false, width, use24Hour = false, onToggleFormat }) => {
    const [formatted24HourTimeInput = 0, m = 0] = value ? value.split(':').map(Number) : [0, 0];
    const { h: displayHours, period } = convertTo12HourFormat(formatted24HourTimeInput);
    const [hourDraft, setHourDraft] = useState(pad(use24Hour ? formatted24HourTimeInput : displayHours));
    const [minDraft, setMinDraft] = useState(pad(m));

    useEffect(() => {
        setHourDraft(pad(use24Hour ? formatted24HourTimeInput : displayHours));
        setMinDraft(pad(m));
    }, [formatted24HourTimeInput, m, use24Hour, displayHours]);

    const emit = (hours24, mins) => {
        onChange(`${pad(hours24)}:${pad(mins)}`);
    };

    const clampHour = (raw) => {
        const parsed = parseInt(raw, 10);
        if (isNaN(parsed)) return raw;
        if (use24Hour) return String(Math.max(0, Math.min(23, parsed)));
        return String(Math.max(1, Math.min(12, parsed)));
    };

    const clampMin = (raw) => {
        const parsed = parseInt(raw, 10);
        if (isNaN(parsed)) return raw;
        return String(Math.max(0, Math.min(59, parsed)));
    };

    const onHourChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw === '') { setHourDraft(''); return; }
        const maxLen2 = raw.slice(0, 2);
        const maxVal = use24Hour ? 23 : 12;
        const num = parseInt(maxLen2, 10);
        if (num > maxVal) {
            setHourDraft(String(maxVal));
        } else {
            setHourDraft(maxLen2);
        }
    };

    const onMinChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw === '') { setMinDraft(''); return; }
        const maxLen2 = raw.slice(0, 2);
        const num = parseInt(maxLen2, 10);
        if (num > 59) {
            setMinDraft('59');
        } else {
            setMinDraft(maxLen2);
        }
    };

    const commitHour = () => {
        const parsed = parseInt(hourDraft, 10);
        if (isNaN(parsed) || hourDraft === '') {
            setHourDraft(pad(use24Hour ? formatted24HourTimeInput : displayHours));
            return;
        }
        const clamped = parseInt(clampHour(hourDraft), 10);
        if (use24Hour) {
            emit(clamped, m);
        } else {
            emit(convertTo24HourFormat(clamped, period), m);
        }
        setHourDraft(pad(clamped));
    };

    const commitMin = () => {
        const parsed = parseInt(minDraft, 10);
        if (isNaN(parsed) || minDraft === '') {
            setMinDraft(pad(m));
            return;
        }
        const clamped = parseInt(clampMin(minDraft), 10);
        emit(formatted24HourTimeInput, clamped);
        setMinDraft(pad(clamped));
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className="custom-date-time-time-row" style={width ? { width } : undefined}>
                <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={hourDraft}
                    onChange={onHourChange}
                    onBlur={commitHour}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitHour(); }}
                    disabled={disabled}
                    className="custom-date-time-inline-input"
                    aria-label="Hours"
                />
                <span className="custom-date-time-separator">:</span>
                <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={minDraft}
                    onChange={onMinChange}
                    onBlur={commitMin}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitMin(); }}
                    disabled={disabled}
                    className="custom-date-time-inline-input"
                    aria-label="Minutes"
                />
                {!use24Hour && (
                    <span
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        onClick={() => { if (!disabled) emit(convertTo24HourFormat(displayHours, period === 'AM' ? 'PM' : 'AM'), m); }}
                        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) emit(convertTo24HourFormat(displayHours, period === 'AM' ? 'PM' : 'AM'), m); }}
                        className={`custom-date-time-period-label${disabled ? ' custom-date-time-period-label--disabled' : ''}`}
                    >
                        {period}
                    </span>
                )}
            </div>
            <Select
                selectedOption={{ label: use24Hour ? '24h' : '12h', value: use24Hour ? '24' : '12' }}
                onChange={({ detail }) => onToggleFormat(detail.selectedOption.value === '24')}
                options={[
                    { label: '12h', value: '12' },
                    { label: '24h', value: '24' }
                ]}
                disabled={disabled}
            />
        </div>
    );
};

const CustomDateTimePicker = ({ value = '', onChange, disabled = false, use24Hour = true }) => {
    const [datePart = '', timePart = ''] = value.split(' ');
    const wrapperRef = useRef(null);
    const [inputWidth, setInputWidth] = useState(null);
    const [localTime, setLocalTime] = useState(timePart || '00:00');
    const [is24Hour, setIs24Hour] = useState(use24Hour);

    useEffect(() => {
        if (wrapperRef.current) {
            const input = wrapperRef.current.querySelector('input');
            if (input) setInputWidth(input.offsetWidth);
        }
    }, [datePart, disabled]);

    useEffect(() => {
        if (timePart) setLocalTime(timePart);
    }, [timePart]);

    const emitCombined = (date, time) => {
        if (!date) {
            onChange('');
            return;
        }
        onChange(`${date} ${time || '00:00'}`);
    };

    const onDateChange = ({ detail }) => {
        emitCombined(detail.value, localTime);
    };

    const onTimeChange = (time) => {
        setLocalTime(time);
        if (datePart) {
            emitCombined(datePart, time);
        }
    };

    return (
        <div ref={wrapperRef}>
            <DatePicker
                onChange={onDateChange}
                value={datePart}
                placeholder="YYYY-MM-DD"
                disabled={disabled}
            />
            <TimePicker
                value={localTime}
                onChange={onTimeChange}
                disabled={disabled}
                width={inputWidth}
                use24Hour={is24Hour}
                onToggleFormat={setIs24Hour}
            />
        </div>
    );
};

export default CustomDateTimePicker;
